import { useEffect, useState } from "react";
import { CalendarPlus, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function CalendarSyncButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" className="border-[#2E3B61] text-slate-200 hover:bg-[#1C2541]" onClick={() => setOpen(true)} data-testid="calendar-sync-button"><CalendarPlus className="w-4 h-4 mr-2" /> Sync to calendar</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1A36] border-[#2E3B61] text-slate-100 max-w-lg" data-testid="calendar-sync-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Subscribe in Outlook, Google or Apple Calendar</DialogTitle>
            <DialogDescription className="text-slate-400">Your court dates and appointments as a live, read-only feed. Refreshes automatically (calendar apps typically poll every few hours).</DialogDescription></DialogHeader>
          {open && <CalendarSyncBody />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CalendarSyncBody() {
  const [sub, setSub] = useState(null);
  useEffect(() => { api.get("/calendar/subscription").then((r) => setSub(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const copy = async () => { await navigator.clipboard.writeText(sub.url); toast.success("Subscription link copied"); };
  const rotate = async () => {
    if (!window.confirm("Generate a new link? Any calendar already subscribed with the old link will stop updating.")) return;
    try { const r = await api.post("/calendar/subscription/rotate"); setSub(r.data); toast.success("New link generated"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!sub) return <p className="text-sm text-slate-400">Loading…</p>;
  const gcal = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(sub.webcal_url)}`;
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input readOnly value={sub.url} className="flex-1 bg-[#0B132B] border border-[#2E3B61] rounded-md px-3 py-2 text-xs font-mono text-slate-300" data-testid="calendar-feed-url" onFocus={(e) => e.target.select()} />
        <Button size="sm" onClick={copy} className="bg-[var(--brand)] text-[#0B132B] hover:bg-[var(--brand-hover)]" data-testid="calendar-copy-button"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <a href={gcal} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-md border border-[#2E3B61] hover:bg-[#1C2541]" data-testid="calendar-google-link"><ExternalLink className="w-4 h-4" /> Add to Google Calendar</a>
        <a href={sub.webcal_url} className="flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-md border border-[#2E3B61] hover:bg-[#1C2541]" data-testid="calendar-webcal-link"><ExternalLink className="w-4 h-4" /> Open in Outlook / Apple</a>
      </div>
      <ol className="text-xs text-slate-400 space-y-1.5 list-decimal pl-4">
        <li><span className="text-slate-200">Google Calendar:</span> Other calendars → <em>+</em> → <em>From URL</em> → paste the link.</li>
        <li><span className="text-slate-200">Outlook:</span> Add calendar → <em>Subscribe from web</em> → paste the link.</li>
        <li><span className="text-slate-200">Apple Calendar:</span> File → <em>New Calendar Subscription</em> → paste the link.</li>
      </ol>
      <div className="flex items-center justify-between pt-2 border-t border-[#2E3B61]/50">
        <p className="text-[11px] text-slate-500">Private link — anyone with it can read your schedule. Times in {sub.timezone}.</p>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-red-300" onClick={rotate} data-testid="calendar-rotate-button"><RefreshCw className="w-3 h-3 mr-1" /> Reset link</Button>
      </div>
    </div>
  );
}
